import React, { useCallback, useRef, useState } from "react";
import { Copy, GripVertical, Lock, LockOpen, Menu, PaintRoller, Settings, Trash2 } from "lucide-react";
import { useIntl } from "react-intl";
import ThicknessPicker from "../../../../../../library/UI/ThicknessPicker";
import LinePalette from "../LinePalette";
import useDrawingToolbarPosition from "../../hooks/useDrawingToolbarPosition";
import DrawingToolbarMenu from "./DrawingToolbarMenu";
import "./DrawingToolbar.sass";

const ICON_SIZE = 16;
const MENU_COLOR = "color";
const MENU_WIDTH = "width";

/**
 * Floating toolbar of the selected drawing (po-chart-visual-spec.md §7.2):
 * ⠿ drags it; 🎨 colour, ≡ width, ⧉ clone, ⚙ settings, 🔒, 🗑.
 *
 * @param {object}   props
 * @param {{ style: { color: string, width: number }, isLocked: boolean }} props.drawing - the selected drawing
 * @param {string[]} props.palette - engine.getLinePalette()
 * @param {{ x: number, y: number }|null} props.savedPosition - CSS px of the chart
 * @param {(position: { x: number, y: number }) => void} props.onSavePosition
 * @param {(change: { color?: string, width?: number }) => void} props.onChangeStyle
 * @param {() => void} props.onClone
 * @param {() => void} props.onToggleLock
 * @param {() => void} props.onRemove
 * @param {() => void} props.onOpenSettings
 */
const DrawingToolbar = ({
  drawing,
  palette,
  savedPosition,
  onSavePosition,
  onChangeStyle,
  onClone,
  onToggleLock,
  onRemove,
  onOpenSettings,
}) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const toolbarRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null);
  const closeMenu = useCallback(() => setOpenMenu(null), []);
  const toggleMenu = (menu) => setOpenMenu((current) => (current === menu ? null : menu));

  const { style: positionStyle, handleProps } = useDrawingToolbarPosition({
    toolbarRef,
    savedPosition,
    onSave: onSavePosition,
    onDragStart: closeMenu,
  });

  const { color, width } = drawing.style;
  const colorLabel = t("futures.chart.indicator_settings.line_color");
  const widthLabel = t("futures.chart.indicator_settings.line_width");
  const lockLabel = t(`futures.chart.drawing_toolbar.${drawing.isLocked ? "unlock" : "lock"}`);
  const LockIcon = drawing.isLocked ? Lock : LockOpen;

  const actions = [
    { key: "clone", label: t("futures.chart.drawing_toolbar.clone"), Icon: Copy, onClick: onClone },
    { key: "settings", label: t("futures.chart.drawing_toolbar.settings"), Icon: Settings, onClick: onOpenSettings },
    { key: "lock", label: lockLabel, Icon: LockIcon, onClick: onToggleLock, isPressed: drawing.isLocked },
    { key: "remove", label: t("futures.chart.drawings.remove"), Icon: Trash2, onClick: onRemove },
  ];

  return (
    <div
      ref={toolbarRef}
      className="drawing-toolbar"
      style={positionStyle}
      role="toolbar"
      aria-label={t("futures.chart.drawing_toolbar.label")}
    >
      {/* Mouse and touch only: the keyboard reaches every action without moving the toolbar */}
      <span className="drawing-toolbar__handle" title={t("futures.chart.drawing_toolbar.move")} {...handleProps}>
        <GripVertical size={ICON_SIZE} aria-hidden="true" />
      </span>
      <DrawingToolbarMenu
        label={colorLabel}
        icon={<PaintRoller size={ICON_SIZE} aria-hidden="true" />}
        marker={<span className="drawing-toolbar__dot" style={{ backgroundColor: color }} aria-hidden="true" />}
        isOpen={openMenu === MENU_COLOR}
        onToggle={() => toggleMenu(MENU_COLOR)}
        onClose={closeMenu}
      >
        <LinePalette
          colors={palette}
          value={color}
          onChange={(nextColor) => onChangeStyle({ color: nextColor })}
          label={colorLabel}
        />
      </DrawingToolbarMenu>
      <DrawingToolbarMenu
        label={`${widthLabel} ${width}px`}
        icon={<Menu size={ICON_SIZE} aria-hidden="true" />}
        marker={
          <span className="drawing-toolbar__badge" aria-hidden="true">
            {width}
          </span>
        }
        isOpen={openMenu === MENU_WIDTH}
        onToggle={() => toggleMenu(MENU_WIDTH)}
        onClose={closeMenu}
      >
        <ThicknessPicker value={width} onChange={(nextWidth) => onChangeStyle({ width: nextWidth })} label={widthLabel} />
      </DrawingToolbarMenu>
      {actions.map(({ key, label, Icon, onClick, isPressed }) => (
        <button
          key={key}
          type="button"
          className={`drawing-toolbar__button drawing-toolbar__button--${key}${isPressed ? " drawing-toolbar__button--pressed" : ""}`}
          aria-label={label}
          title={label}
          aria-pressed={isPressed}
          onClick={onClick}
        >
          <Icon size={ICON_SIZE} aria-hidden="true" />
        </button>
      ))}
    </div>
  );
};

export default DrawingToolbar;
